import { useEffect, useState, useRef } from 'react';

import qs from 'query-string';
import { useHistory } from 'react-router-dom';
import { getQueryString } from 'lane-shared/helpers';

export type QueryStringResult<T> = [
  T,
  (props: any) => void,
  (props: any) => string,
  (props: any) => void,
];

export interface QueryObject {
  [key: string]: Date | string | number | boolean | null;
}
/**
 * Listens for a changes to the query string, and updates the state with a
 * parsed query object.
 */
/**
 * Custom hook for managing query strings in the URL.
 *
 * @template T - The type of the query object.
 * @param {QueryObject} defaultQuery - The default query object.
 * @param {string[]} [keepedKeys] - An optional array of keys to keep in the query object.
 *                                 One use case is cleanup query string when switch between different tabStrips
 * @param {Location} [windowLocation] - Optional window.location object for testing purposes
 * @returns {QueryStringResult<T>} - An array containing the query object, a function to update the query object, and a function to generate the URL with the updated query object.
 */
export function useQueryString<
  T extends {
    [x: string]:
      | string
      | number
      | boolean
      | Date
      | (string | number | boolean)[]
      | null
      | undefined;
  },
>(
  defaultQuery: QueryObject = {},
  keepedKeys?: string[],
  windowLocation: Location = window.location
): QueryStringResult<T> {
  const history = useHistory();

  const searches = qs.parse(history.location.search, {});
  const filteredQuery = keepedKeys
    ? Object.keys(searches)
        .filter(key => keepedKeys.includes(key))
        .reduce(
          (obj, key) => {
            obj[key] = searches[key];

            return obj;
          },
          {} as { [key: string]: any }
        )
    : searches;

  const initialQuery = useRef({
    ...defaultQuery,
    ...filteredQuery,
  }).current;

  const [query, setQuery] = useState(initialQuery);

  const mountedPathname = useRef(windowLocation.pathname).current;

  useEffect(() => {
    goToUrl(initialQuery);
  }, []);

  useEffect(() => {
    const listener = history.listen(location => {
      if (location.pathname === mountedPathname && location.search === '') {
        setQuery({
          ...initialQuery,
        });
        goToUrl(initialQuery);
      } else {
        setQuery({
          ...qs.parse(location.search),
        });
      }
    });

    return () => {
      if (listener) {
        listener();
      }
    };
  }, []);

  function makeUrl(props: any) {
    return (
      history.location.pathname +
      getQueryString({
        ...query,
        ...props,
      })
    );
  }

  function goToUrl(props: any) {
    const nextUrl = makeUrl(props);

    const newUrl = new URL(windowLocation.origin + nextUrl);

    if (
      newUrl.pathname === windowLocation.pathname &&
      newUrl.search === windowLocation.search
    ) {
      return;
    }

    // history.replace() expects a path string (e.g., "/path?query"), not a full URL
    const pathWithSearch = newUrl.pathname + newUrl.search;

    history.replace(pathWithSearch, history?.location?.state);
  }

  function goToUrlByQueryParams(queryParams: Record<string, string>) {
    const { pathname, search, origin } = windowLocation;

    // ✅ Safely build encoded query params
    const params = new URLSearchParams();

    Object.entries(queryParams || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        // Caller is responsible for string conversion, but handle primitives safely
        const stringValue = typeof value === 'string' ? value : String(value);

        params.set(key, stringValue);
      }
    });

    const newSearch = params.toString();
    const nextUrl = newSearch ? `${pathname}?${newSearch}` : pathname;
    const newUrl = new URL(nextUrl, origin);

    // 🚫 Avoid redundant updates
    if (newUrl.pathname === pathname && newUrl.search === search) return;

    // ✅ Update history safely (relative path only)
    history.replace(
      `${newUrl.pathname}${newUrl.search}`,
      history?.location?.state
    );
  }

  return [query as any as T, goToUrl, makeUrl, goToUrlByQueryParams];
}

export default useQueryString;
